/**
 * Configuration Debugging Utility
 * Helper functions to verify environment configuration at runtime
 */

import { getEnvironmentConfig } from "@/lib/env";

/**
 * Log current environment configuration to console
 * Useful for verifying correct Supabase backend is connected
 */
export function logEnvironmentConfig(): void {
  const config = getEnvironmentConfig();

  console.group("🔧 Environment Configuration");
  console.log("Environment:", config.environment);
  console.log("Frontend URL:", config.frontendUrl);
  console.log("Supabase URL:", config.supabase.url);
  console.log(
    "Supabase Project ID:",
    config.supabase.url.match(/https:\/\/([^.]+)\.supabase\.co/)?.[1] ||
      "unknown"
  );
  console.log("Debug Mode:", config.debug);
  console.log("API Timeout:", config.apiTimeout + "ms");
  console.groupEnd();
}

/**
 * Verify that current environment matches expected Supabase backend
 * @param expectedProjectId - Expected Supabase project reference ID (empty string to skip verification)
 * @returns true if environment is correctly configured or if verification is skipped
 */
export function verifyEnvironment(expectedProjectId: string): boolean {
  // Skip verification if no expected project ID is provided
  if (!expectedProjectId || expectedProjectId.trim() === '') {
    return true;
  }

  const config = getEnvironmentConfig();
  const actualProjectId = config.supabase.url.match(
    /https:\/\/([^.]+)\.supabase\.co/
  )?.[1];

  if (actualProjectId !== expectedProjectId) {
    console.error(
      `❌ Environment mismatch!\n` +
        `   Expected: ${expectedProjectId}\n` +
        `   Actual: ${actualProjectId}\n` +
        `   This build may be connecting to the wrong Supabase backend.`
    );
    return false;
  }

  console.log(`✅ Environment verified: ${actualProjectId}`);
  return true;
}

/**
 * Get a short summary of current environment configuration
 */
export function getConfigSummary(): string {
  const config = getEnvironmentConfig();
  const projectId =
    config.supabase.url.match(/https:\/\/([^.]+)\.supabase\.co/)?.[1] ||
    "unknown";

  return `${config.environment.toUpperCase()} (${projectId})`;
}

/**
 * Display environment banner in console (development only)
 */
export function showEnvironmentBanner(): void {
  if (!import.meta.env.DEV) return;

  const config = getEnvironmentConfig();
  const projectId =
    config.supabase.url.match(/https:\/\/([^.]+)\.supabase\.co/)?.[1] ||
    "unknown";

  const banner = `
╔════════════════════════════════════════════════════════════╗
║  thepearl - ${config.environment.toUpperCase().padEnd(48)}║
║                                                            ║
║  Supabase: ${projectId.padEnd(47)}║
║  Frontend: ${config.frontendUrl.padEnd(47)}║
╚════════════════════════════════════════════════════════════╝
  `;

  console.log(banner);
}
